/**
 * Soft shadow filter (playtest 2026-08-01: "nice soft shadows rather than
 * harsh simple ones"), with CONTACT HARDENING (playtest 2026-08-02: "the shadow
 * on the cyclist is a bit too soft, can't see the moving feet").
 *
 * WHY A CUSTOM FILTER. three's WebGPU backend ships three usable directional
 * shadow filters and none of them is what this game wants:
 *
 *  - `PCFSoftShadowFilter` (renderer.shadowMap.type = 2, what we shipped) is a
 *    fixed 2x2-gather bilinear tap. Its penumbra is ONE texel — at the world
 *    frustum's ~3.5 cm texels that is a hard edge, and it ignores
 *    `light.shadow.radius` entirely, so there is no dial to turn.
 *  - `PCFShadowFilter` (type 1) IS radius-aware, but spends only FIVE taps on
 *    the disk and hides the undersampling behind a per-pixel IGN rotation. At
 *    the radius this scene needs (6+ texels) the rotation stops hiding
 *    anything: the rider's own shadow — a metre-wide silhouette right under the
 *    camera — came out visibly stippled, a moving dither crawl on the road.
 *  - `VSMShadowFilter` is genuinely smooth but wants two extra full-screen blur
 *    passes over the 2048² map every frame plus an RG16F copy of it — the wrong
 *    trade on a mobile-class GPU — and Chebyshev light-bleeding is worst exactly
 *    where this scene stacks casters at very different depths (a bridge tower
 *    over the deck). Not adopted; revisit only if the tap count below ever
 *    becomes the bottleneck.
 *
 * WHAT THIS IS. A two-stage PCSS-lite:
 *
 *  1. BLOCKER SEARCH (`SEARCH_TAPS` point fetches of the raw shadow depth over
 *     a disk of `shadow.radius` texels). The taps that are in front of the
 *     receiver are averaged into a mean blocker depth.
 *  2. PCF (`TAPS` Vogel-disk hardware depth COMPARES, IGN-rotated per pixel so
 *     the residual error reads as fine grain rather than as concentric rings)
 *     over a disk whose radius is driven by the BLOCKER-TO-RECEIVER GAP: about
 *     `MIN_RADIUS_TEXELS` where the caster touches the receiver, ramping to the
 *     full `shadow.radius` once the gap reaches `FULL_PENUMBRA_GAP_M`.
 *
 * WHY THE HARDENING. The fixed 5-texel blur is right for a building's roofline
 * 30 m away and wrong for the rider's own feet: the pedals sit ~0.3 m over the
 * road, so at the sun's ~30° elevation their shadow lands ~0.5 m from the tyre
 * contact patch and the whole leg silhouette lives inside a ~0.6 m
 * blocker-receiver gap. A 5-texel (≈18 cm) penumbra smears the two cranks into
 * one blob — and the pedalling feet are the ONE thing the hero shadow exists to
 * show (render/riderCaster.ts). With the ramp below the feet get ~2 texels
 * (≈7 cm) and read as two separate legs, while a building 26 m up still gets
 * the full soft edge.
 *
 * ORTHO MATH. The light is directional, so light-space depth is LINEAR: the
 * normalized gap between blocker and receiver times the shadow camera's
 * (far − near) IS the world gap in metres. That range is not reachable from a
 * `filterNode`, so render/riderCaster.ts pushes it in through
 * `setShadowDepthRange()` — one uniform, no recompile.
 *
 * COST. `SEARCH_TAPS` (8) `textureLoad` fetches — no sampler, no filtering —
 * plus `TAPS` (16) depth compares, in ONE pass, with no extra render targets and
 * no extra memory. The search fetches are the cheapest kind of texture read
 * there is; the compares are the same 16 the previous build already paid for.
 *
 * CONTROL FLOW. Everything below is straight-line arithmetic — no `If`. WGSL
 * requires uniform control flow for `textureSampleCompare`, and the filter is
 * invoked from inside the lighting model, so a data-dependent branch here would
 * be a validation error rather than an optimisation.
 *
 * The maximum disk radius comes from `light.shadow.radius`, exactly as the
 * built-in does, so `SHADOW_RADIUS` in render/riderCaster.ts stays the single
 * dial for the SOFT end of the ramp.
 */

import {
  Fn,
  float,
  ivec2,
  max,
  min,
  mix,
  reference,
  renderGroup,
  screenCoordinate,
  step,
  texture,
  textureLoad,
  uniform,
  vec2,
} from "three/tsl";
import { interleavedGradientNoise, vogelDiskSample } from "three/tsl";

/**
 * Vogel disk sample count for the PCF pass. 16 at SHADOW_RADIUS = 5 puts roughly
 * three samples per texel of the disk, which is where the dither the built-in
 * 5-tap filter showed on the rider's shadow disappears entirely.
 */
export const TAPS = 16;

/**
 * Blocker-search sample count. 8 is the low end of what still gives a stable
 * mean: the estimate only has to be good enough to pick a radius, and the PCF
 * pass that follows hides its error. Doubling it changed nothing visible in the
 * rider shadow and cost 8 more fetches per shadowed fragment.
 */
export const SEARCH_TAPS = 8;

/**
 * Penumbra floor, in shadow-map texels. Not 0: a hardware compare is still a
 * 4-texel bilinear tap, so below ~0.5 texel of disk the filter buys nothing but
 * aliasing on the tyre contact patch. At the fitted frustum's ~3 cm texels this
 * is a ~2 cm penumbra — a contact shadow. Verified against a `shadow.radius =
 * 0.5` reference frame: at that radius the whole silhouette (head, arms, both
 * cranks) is legible, i.e. the 2048² map is NOT the limit here — the filter
 * radius was.
 */
export const MIN_RADIUS_TEXELS = 0.7;

/**
 * Blocker-to-receiver gap (metres, measured along the light direction) at which
 * the disk reaches the full `shadow.radius`.
 *
 * PHYSICALLY this should be ~18 m: a real sun subtends 0.53°, so a penumbra of
 * SHADOW_RADIUS (5 texels ≈ 17 cm) is reached at gap ≈ 0.17 / tan(0.53°). At
 * that scale, though, EVERYTHING at road level goes crisp and the build loses
 * the soft look the 2026-08-01 playtest asked for. 9 m is the artistic
 * compromise, chosen against the rider's own geometry: the sun sits ~30° up, so
 * a caster point h metres over the road is 2h from its own shadow along the
 * light. That puts the pedals (h ≈ 0.3) at a ~1-texel contact edge — two
 * separate cranks — the head (h ≈ 1.7) at ~2.3, a car roof at ~2.1, and a tree
 * crown or a building roofline (h ≥ 8) at the full soft 5.
 */
export const FULL_PENUMBRA_GAP_M = 9;

const TWO_PI = 6.28318530718;

/**
 * Shadow-camera depth range (far − near, metres) — the scale that turns a
 * normalized depth gap into a world gap. Written once by
 * render/riderCaster.ts; a uniform, so changing it never rebuilds a shader.
 */
const depthRangeM = uniform(1);

/**
 * Tell the filter how deep the shadow camera's ortho frustum is, in metres
 * (`camera.far - camera.near`). Call once after the shadow camera is fitted —
 * the contact-hardening ramp is expressed in METRES of blocker-receiver gap and
 * cannot convert normalized depth without it.
 */
export function setShadowDepthRange(metres: number): void {
  (depthRangeM as unknown as { value: number }).value = metres;
}

/**
 * The filter function, in three's `shadow.filterNode` shape. Assign it to a
 * light's `shadow.filterNode` — it then wins over `renderer.shadowMap.type`
 * for that light only.
 *
 * NOTE on the types: three's TSL builder generics are extremely expensive to
 * infer through a chain like this (see the same note in render/plastic.ts), so
 * the node plumbing below is deliberately erased with `never`/`unknown` casts.
 */
export const softShadowFilter = Fn((inputs: unknown, builder: unknown) => {
  const { depthTexture, shadowCoord, shadow, depthLayer } = inputs as {
    depthTexture: unknown;
    shadowCoord: { xy: unknown; z: unknown };
    shadow: unknown;
    depthLayer: unknown;
  };
  /**
   * Reversed depth (three's default on WebGPU where supported) puts the near
   * plane at 1 and the far plane at 0, so BOTH the "is this tap a blocker" test
   * and the sign of the gap flip. The PCF compares are unaffected — three's
   * `compare()` already accounts for it.
   */
  const reversed =
    (builder as { renderer?: { reversedDepthBuffer?: boolean } } | undefined)?.renderer
      ?.reversedDepthBuffer === true;

  const isArray = (depthTexture as { isArrayTexture?: boolean }).isArrayTexture === true;
  const compareAt = (uv: unknown): unknown => {
    let d: unknown = texture(depthTexture as never, uv as never);
    if (isArray) d = (d as { depth(l: unknown): unknown }).depth(depthLayer);
    return (d as { compare(z: unknown): unknown }).compare(shadowCoord.z);
  };

  const mapSize: unknown = (reference as unknown as (n: string, t: string, o: unknown) => unknown)(
    "mapSize",
    "vec2",
    shadow,
  );
  const radius: unknown = (reference as unknown as (n: string, t: string, o: unknown) => unknown)(
    "radius",
    "float",
    shadow,
  );
  const grouped = (n: unknown): unknown =>
    (n as { setGroup(g: unknown): unknown }).setGroup(renderGroup);
  const size: unknown = grouped(mapSize);
  const texelSize: unknown = (vec2(1) as unknown as { div(v: unknown): unknown }).div(size);
  const maxRadius: unknown = grouped(radius);
  /** The search disk: the widest penumbra the PCF pass can ask for. */
  const searchScaled: unknown = (maxRadius as { mul(v: unknown): unknown }).mul(
    (texelSize as { x: unknown }).x,
  );

  // Per-pixel rotation of the disk — without it the sparse ring structure of a
  // Vogel set is visible as banding around a shadow edge. Shared by both passes
  // so a blocker found by the search is a blocker the PCF will also see.
  const phi: unknown = (
    interleavedGradientNoise(screenCoordinate.xy as never) as unknown as {
      mul(v: number): unknown;
    }
  ).mul(TWO_PI);

  // --- Stage 1: blocker search ---------------------------------------------
  //
  // Raw (unfiltered, sampler-free) fetches of the shadow map. `textureLoad`
  // rather than a sampled read on purpose: the depth texture is bound with a
  // COMPARISON sampler for the PCF pass, and a point fetch needs neither a
  // second filtering mode nor uniform control flow.
  const clampCoord = (uv: unknown): unknown => {
    const px: unknown = (uv as { mul(v: unknown): unknown }).mul(size);
    const lim: unknown = (size as { sub(v: number): unknown }).sub(1);
    const clamped: unknown = min(
      max(px as never, vec2(0) as never) as never,
      lim as never,
    );
    return ivec2(clamped as never);
  };
  const loadDepth = (uv: unknown): unknown => {
    let d: unknown = textureLoad(depthTexture as never, clampCoord(uv) as never);
    if (isArray) d = (d as { depth(l: unknown): unknown }).depth(depthLayer);
    return d;
  };

  const zRef: unknown = shadowCoord.z;
  let blockerSum: unknown = float(0);
  let blockerCount: unknown = float(0);
  for (let i = 0; i < SEARCH_TAPS; i++) {
    const offset = (
      vogelDiskSample(i as never, SEARCH_TAPS as never, phi as never) as unknown as {
        mul(v: unknown): unknown;
      }
    ).mul(searchScaled);
    const uv = (shadowCoord.xy as { add(v: unknown): unknown }).add(offset);
    const d = loadDepth(uv);
    // 1 when this tap is BETWEEN the light and the receiver, i.e. a blocker.
    const isBlocker: unknown = reversed
      ? step(zRef as never, d as never)
      : step(d as never, zRef as never);
    blockerCount = (blockerCount as { add(v: unknown): unknown }).add(isBlocker);
    blockerSum = (blockerSum as { add(v: unknown): unknown }).add(
      (d as { mul(v: unknown): unknown }).mul(isBlocker),
    );
  }
  // Mean blocker depth. With no blockers the mean is 0 (or 1 under reversed
  // depth), which drives the ramp to the SOFT end — the conservative choice,
  // and invisible because the PCF pass then returns fully lit anyway.
  const meanBlocker: unknown = (blockerSum as { div(v: unknown): unknown }).div(
    max(blockerCount as never, float(1) as never),
  );

  // --- Stage 2: contact-hardened PCF ---------------------------------------
  //
  // The frustum is ORTHOGRAPHIC, so normalized depth is linear in metres and
  // the gap scales by the camera's depth range directly.
  const gapNorm: unknown = reversed
    ? (meanBlocker as { sub(v: unknown): unknown }).sub(zRef)
    : (zRef as { sub(v: unknown): unknown }).sub(meanBlocker);
  const gapM: unknown = (gapNorm as { mul(v: unknown): unknown }).mul(depthRangeM);
  const k: unknown = (
    min(
      max((gapM as { div(v: number): unknown }).div(FULL_PENUMBRA_GAP_M) as never, float(0) as never),
      float(1) as never,
    ) as unknown as object
  );
  const radiusTexels: unknown = mix(
    float(MIN_RADIUS_TEXELS) as never,
    maxRadius as never,
    k as never,
  );
  const radiusScaled: unknown = (radiusTexels as unknown as { mul(v: unknown): unknown }).mul(
    (texelSize as { x: unknown }).x,
  );

  let sum: unknown = float(0);
  for (let i = 0; i < TAPS; i++) {
    const offset = (
      vogelDiskSample(i as never, TAPS as never, phi as never) as unknown as {
        mul(v: unknown): unknown;
      }
    ).mul(radiusScaled);
    const uv = (shadowCoord.xy as { add(v: unknown): unknown }).add(offset);
    sum = (sum as { add(v: unknown): unknown }).add(compareAt(uv));
  }
  return (sum as { mul(v: number): unknown }).mul(1 / TAPS);
});
